/**
 * SpiceDeclineForm - "The client declined today": a refusal is still a
 * session. It lives on the client record's Notes tab (SPICE face), under
 * the checkbox line of that name above the composer, and builds the note
 * from the three required facts (spiceVisitNote.ts's builder).
 *
 * Nothing posts here. The form hands the decline to the composer on the
 * same page, the way every note is written (founder ruling: SPICE notes
 * follow the same path as care-management notes): it writes a notePrefill
 * (the decline text, 1 hour, service 'spice') and the host remounts the
 * composer, which picks it up. There she picks SPICE or Friendly Visitor
 * and the date, and posts; the read shows "Reading your note..." and the
 * decline waits in the confirm queue. The note bills at the fixed decline
 * line (the server's SPICE_DECLINE_INVOICE_LINE, keyed on its first line).
 *
 * The three facts are the host's (LP-28): unticking the line hides this
 * form and ticking it again shows the same words back, so nothing typed
 * is ever discarded by a tick. No card and no chrome of its own: one
 * quiet sentence, the three fields and the buttons, inline under the
 * line. The first field takes focus when the form opens.
 */

import { type ReactNode } from 'react';
import { Button } from '@/components/primitives/Button';
import { FLD, FLD_LABEL, FLD_TEXTAREA } from '@/components/primitives/modalFormStyles';
import { writeNotePrefill } from '@/lib/notePrefill';
import {
  buildSpiceDeclineNoteText,
  SPICE_VISIT_DEFAULT_HOURS,
  type SpiceDeclineFacts,
  spiceDeclineFactsAreComplete,
} from '@/lib/spiceVisitNote';

/** The form's id, for the line that reveals it. */
export const SPICE_DECLINE_FORM_ID = 'spice-decline-form';

/** The three facts before anything is typed. */
export const EMPTY_SPICE_DECLINE_FACTS: SpiceDeclineFacts = {
  whatWasTried: '',
  whatTheySaid: '',
  timeOfDay: '',
};

export interface SpiceDeclineFormProps {
  tenantSlug: string;
  clientId: string;
  /** The three facts, held by the host so a hidden form keeps its words. */
  facts: SpiceDeclineFacts;
  onFactsChange: (facts: SpiceDeclineFacts) => void;
  /** The decline is in the prefill: the host remounts its composer. */
  onHandedToComposer: () => void;
  onCancel: () => void;
}

export function SpiceDeclineForm({
  tenantSlug,
  clientId,
  facts,
  onFactsChange,
  onHandedToComposer,
  onCancel,
}: SpiceDeclineFormProps): ReactNode {
  const complete = spiceDeclineFactsAreComplete(facts);

  const handOver = (): void => {
    writeNotePrefill(tenantSlug, clientId, {
      narrative: buildSpiceDeclineNoteText(facts),
      hours: SPICE_VISIT_DEFAULT_HOURS,
      source: 'spice_decline',
      service: 'spice',
    });
    onHandedToComposer();
  };

  return (
    <form
      id={SPICE_DECLINE_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        if (complete) handOver();
      }}
      className="space-y-3"
      data-testid="spice-decline-form"
    >
      <p className="text-sm leading-relaxed text-ink-soft">
        A declined visit still counts as a session, so it goes on the invoice as usual.
      </p>
      <div>
        <label htmlFor="spice-decline-tried" className={FLD_LABEL}>
          What was tried
        </label>
        <textarea
          id="spice-decline-tried"
          value={facts.whatWasTried}
          onChange={(e) => onFactsChange({ ...facts, whatWasTried: e.target.value })}
          rows={2}
          className={FLD_TEXTAREA}
          data-testid="spice-decline-tried"
          // The line that reveals this form is the control the person
          // just used; the first field takes the focus from it.
          autoFocus
        />
      </div>
      <div>
        <label htmlFor="spice-decline-said" className={FLD_LABEL}>
          What they said
        </label>
        <textarea
          id="spice-decline-said"
          value={facts.whatTheySaid}
          onChange={(e) => onFactsChange({ ...facts, whatTheySaid: e.target.value })}
          rows={2}
          className={FLD_TEXTAREA}
          data-testid="spice-decline-said"
        />
      </div>
      <div>
        <label htmlFor="spice-decline-time" className={FLD_LABEL}>
          Time of day
        </label>
        <input
          id="spice-decline-time"
          type="text"
          value={facts.timeOfDay}
          onChange={(e) => onFactsChange({ ...facts, timeOfDay: e.target.value })}
          placeholder="e.g. mid-morning, around 10:30"
          className={FLD}
          data-testid="spice-decline-time"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="pepper" disabled={!complete} data-testid="spice-file-decline">
          Add to the note
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="spice-decline-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}
